import { useState, useEffect, useCallback } from 'react'
import { MessageSquare, ArrowLeft, Lock, Loader2, Search } from 'lucide-react'
import ChatPanel, { ChatLockedState } from '../components/ChatPanel'
import { chatApi } from '../lib/chatApi'
import { useAuth } from '../hooks/useAuth'
import Header from '../components/layout/Header'

const fmtDate = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export default function ChatScreen({ onNavigate }) {
  const { user, profile, company, role, signOut } = useAuth()
  const [conversations, setConversations] = useState([])
  const [activeConv, setActiveConv] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')
  const [lockReason, setLockReason] = useState(null)

  const userId = user?.id
  const userRole = profile?.role || role || 'founder'

  const loadConversations = useCallback(async () => {
    if (!userId) { setLoading(false); return }
    const { data, error } = await chatApi.listConversations(userId, userRole)
    if (error) { setError(error); setLoading(false); return }
    setConversations(data)
    setLoading(false)
  }, [userId, userRole])

  useEffect(() => {
    loadConversations()
    const interval = setInterval(loadConversations, 15000)
    return () => clearInterval(interval)
  }, [loadConversations])

  const filtered = query.trim()
    ? conversations.filter((c) =>
        [c.agent_email, c.status].join(' ').toLowerCase().includes(query.toLowerCase()),
      )
    : conversations

  const handleSelectConv = async (conv) => {
    if (userRole === 'founder') {
      if (conv.agent_request_id) {
        const { allowed, reason } = await chatApi.canInitiateChat(userId, conv.agent_request_id)
        if (!allowed) {
          setActiveConv(null)
          setLockReason(reason)
          return
        }
      }
    }
    setLockReason(null)
    setActiveConv(conv)
  }

  const handleLogout = () => {
    signOut?.()
    onNavigate?.('login')
  }

  // Get the correct role label for the header
  const getRoleLabel = () => {
    if (userRole === 'founder') return 'founder'
    if (userRole === 'admin') return 'admin'
    if (userRole === 'staff') return 'staff'
    return 'founder'
  }

  return (
    <div className="flex flex-col h-full">
      

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-neutral-50 p-4 sm:p-8 lg:p-10">
        <div className="space-y-4 animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-ink">Messages</h1>
            <p className="text-sm text-muted mt-1">Secure chat with your assigned Agent. Messaging is enabled once an Agent accepts your request.</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 h-[calc(100vh-280px)] min-h-[400px]">
            {/* Conversation List */}
            <div className="w-full lg:w-80 shrink-0 bg-white border border-neutral-200 rounded-xl flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-neutral-200">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border-0 border-b border-neutral-200 focus:border-brand focus:outline-none focus:ring-0 placeholder:text-neutral-400"
                    placeholder="Search conversations..."
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loading && (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 size={20} className="animate-spin text-neutral-400" />
                  </div>
                )}
                {!loading && filtered.length === 0 && (
                  <div className="text-center py-10 px-4">
                    <MessageSquare size={28} className="text-neutral-300 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-ink">No conversations yet</p>
                    <p className="text-xs text-muted mt-1">Conversations appear here once an Agent is assigned to your request.</p>
                  </div>
                )}
                {!loading && filtered.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => handleSelectConv(conv)}
                    className={`w-full px-4 py-3 text-left border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${
                      activeConv?.id === conv.id ? 'bg-brand-soft' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-ink truncate">
                        {userRole === 'founder' ? conv.agent_email || 'Agent' : 'Founder'}
                      </span>
                      <span className="text-[10px] text-muted shrink-0 ml-2">{fmtDate(conv.created_at)}</span>
                    </div>
                    <p className="text-xs text-muted truncate">
                      Status: <span className="font-medium text-ink">{conv.status}</span>
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Chat Panel */}
            <div className="flex-1 bg-white border border-neutral-200 rounded-xl overflow-hidden">
              {lockReason ? (
                <ChatLockedState reason={lockReason} />
              ) : activeConv ? (
                <ChatPanel
                  conversationId={activeConv.id}
                  onBack={() => setActiveConv(null)}
                  agentEmail={activeConv.agent_email}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mb-4">
                    <MessageSquare size={28} className="text-neutral-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-ink mb-2">Select a conversation</h3>
                  <p className="text-xs text-muted max-w-sm">Choose a conversation from the list to start messaging with your assigned Agent.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}